import type {
  ConnectionPoint,
  Point,
  ShapeStyle,
  ShapeType,
  Size,
} from '../state/types';
import { buildEdgeMidpoints } from './connectionPoints';
import { Shape } from './Shape';

export class Diamond extends Shape {
  readonly type: ShapeType = 'diamond';

  constructor(
    position: Point,
    size: Size,
    style?: Partial<ShapeStyle>,
    text = ''
  ) {
    super(position, size, style, text);
  }

  containsPoint(p: Point): boolean {
    const center = this.getCenter();
    const hw = this.size.width / 2;
    const hh = this.size.height / 2;

    // Diamond is defined by |x-cx|/hw + |y-cy|/hh <= 1
    const dx = Math.abs(p.x - center.x) / hw;
    const dy = Math.abs(p.y - center.y) / hh;
    return dx + dy <= 1;
  }

  getConnectionPoints(): ConnectionPoint[] {
    const center = this.getCenter();
    const hw = this.size.width / 2;
    const hh = this.size.height / 2;
    const edgeMidpoints = buildEdgeMidpoints(this.id, this.position, this.size);

    return [
      ...edgeMidpoints,
      // Edge midpoints
      {
        id: `${this.id}-top-right`,
        position: { x: center.x + hw / 2, y: center.y - hh / 2 },
        direction: 'top',
      },
      {
        id: `${this.id}-bottom-right`,
        position: { x: center.x + hw / 2, y: center.y + hh / 2 },
        direction: 'right',
      },
      {
        id: `${this.id}-bottom-left`,
        position: { x: center.x - hw / 2, y: center.y + hh / 2 },
        direction: 'bottom',
      },
      {
        id: `${this.id}-top-left`,
        position: { x: center.x - hw / 2, y: center.y - hh / 2 },
        direction: 'left',
      },
    ];
  }

  render(ctx: CanvasRenderingContext2D): void {
    const center = this.getCenter();
    const hw = this.size.width / 2;
    const hh = this.size.height / 2;

    ctx.save();
    ctx.globalAlpha = this.style.opacity;

    ctx.beginPath();
    ctx.moveTo(center.x, center.y - hh); // Top
    ctx.lineTo(center.x + hw, center.y); // Right
    ctx.lineTo(center.x, center.y + hh); // Bottom
    ctx.lineTo(center.x - hw, center.y); // Left
    ctx.closePath();

    ctx.fillStyle = this.style.fill;
    ctx.fill();

    ctx.strokeStyle = this.style.stroke;
    ctx.lineWidth = this.style.strokeWidth;
    ctx.stroke();

    ctx.restore();

    this.renderText(ctx);
    this.renderSelection(ctx);
  }
}
